import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { BrainCircuit, MessagesSquare, AlertTriangle, ClipboardList, TrendingUp, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConversationView from "./ConversationView";

const WINDOWS = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];
const RTYPE_LABEL = { maintenance: "Maintenance", rsvp: "Event RSVP", security_incident: "Security", dues: "Dues" };

const Bars = ({ items, colorFrom, empty, onBarClick }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <div className="text-sm text-ink/40 py-6 text-center">{empty}</div>;
  return (
    <div className="space-y-2.5">
      {items.map((i) => {
        const clickable = !!onBarClick;
        const Row = (
          <>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-ink/80 font-medium truncate pr-2 flex items-center gap-1">
                {i.label}{clickable && <ChevronRight className="h-3.5 w-3.5 text-ink/30" />}
              </span>
              <span className="text-ink/50 tabular-nums shrink-0">{i.count}</span>
            </div>
            <div className="h-2 rounded-full bg-sand-100 overflow-hidden">
              <div className={`h-full rounded-full ${colorFrom}`} style={{ width: `${Math.round((i.count / max) * 100)}%` }} />
            </div>
          </>
        );
        return clickable ? (
          <button key={i.key} data-testid={`bar-${i.key}`} onClick={() => onBarClick(i)}
            className="w-full text-left group hover:opacity-90 transition-opacity">{Row}</button>
        ) : <div key={i.key} data-testid={`bar-${i.key}`}>{Row}</div>;
      })}
    </div>
  );
};

const fmtDate = (d) => { try { return new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

export default function PlatformIntelligence() {
  const { actualRole, enterCommunity, viewCompanyId } = useDash();
  const isCompany = actualRole === "company_admin" || actualRole === "regional_manager";
  const scoped = isCompany || !!viewCompanyId;
  const [days, setDays] = useState(30);
  const [data, setData] = useCachedState(`intel:${scoped ? "s" : "a"}:${viewCompanyId || ""}:${days}`, null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);          // { key, label }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [selConv, setSelConv] = useState(null);

  const base = scoped ? "/intelligence" : "/admin/intelligence";
  const cq = viewCompanyId ? `&company_id=${viewCompanyId}` : "";

  useEffect(() => {
    setLoading(true);
    api.get(scoped ? `/intelligence?days=${days}${cq}` : `/admin/intelligence?days=${days}`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, scoped, viewCompanyId]);

  const openTopic = (item) => {
    setDrill(item); setDrillData(null); setDrillLoading(true);
    api.get(`${base}/topic?topic=${item.key}&days=${days}${cq}`)
      .then((r) => setDrillData(r.data)).catch(() => setDrillData({ conversations: [] })).finally(() => setDrillLoading(false));
  };

  const t = data?.totals || {};
  return (
    <div className="max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <PageTitle title={<><BrainCircuit className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {scoped ? "Portfolio Intelligence" : "Platform Intelligence"}</>} about={<>{scoped
            ? "What residents ask, which requests are surging, and why conversations escalate — across every community you manage."
            : "The portfolio-wide brain — what residents ask, which requests are surging, and why conversations escalate, across every community."} <span className="text-ink/40">Tap a topic to read the conversations behind it.</span></>} />
        </div>
        <div className="flex gap-2">
          {WINDOWS.map(([v, l]) => (
            <button key={v} data-testid={`intel-window-${v}`} onClick={() => setDays(v)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${days === v ? "bg-ink text-white" : "bg-white border border-sand-200 hover:border-ink text-ink/70"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[["Conversations", t.conversations, MessagesSquare, "bg-sand-100 text-ink"],
              ["Escalations", t.escalations, AlertTriangle, "bg-amber-100 text-amber-700"],
              ["Requests logged", t.requests, ClipboardList, "bg-blue-100 text-blue-700"]].map(([l, n, Icon, cls]) => (
              <div key={l} className="bg-white rounded-2xl border border-sand-200 p-5">
                <div className={`inline-flex h-9 w-9 rounded-xl items-center justify-center mb-3 ${cls}`}><Icon className="h-4.5 w-4.5" strokeWidth={1.75} /></div>
                <div className="font-heading text-4xl font-extrabold tracking-tight leading-none">{n ?? 0}</div>
                <div className="text-xs text-ink/50 mt-1 uppercase tracking-[0.1em] font-bold">{l} · last {days}d</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-sand-200 p-5">
              <div className="font-heading font-bold mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-redline-500" /> Top resident topics</div>
              <Bars items={data.topics} colorFrom="bg-redline-500" onBarClick={openTopic} empty="No tagged conversations yet — tags accrue as residents chat." />
            </div>
            <div className="bg-white rounded-2xl border border-sand-200 p-5">
              <div className="font-heading font-bold mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Why conversations escalate</div>
              <Bars items={data.escalations} colorFrom="bg-amber-500" empty="No escalations in this window." />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-sand-200 p-5">
            <div className="font-heading font-bold mb-3">Requests by type</div>
            {data.request_types.length ? (
              <div className="flex flex-wrap gap-2">
                {data.request_types.map((r) => (
                  <span key={r.key} className="inline-flex items-center gap-2 rounded-full bg-sand-100 px-4 py-2 text-sm font-semibold text-ink">
                    {RTYPE_LABEL[r.key] || r.key} <span className="text-ink/50">{r.count}</span>
                  </span>
                ))}
              </div>
            ) : <div className="text-sm text-ink/40 py-3">No requests logged in this window.</div>}
          </div>

          <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
            <div className="font-heading font-bold px-5 pt-5 pb-3">Communities at a glance</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink/50 border-b border-sand-100">
                  <th className="font-semibold px-5 py-2.5">Community</th>
                  <th className="font-semibold px-5 py-2.5 text-right">Conversations</th>
                  <th className="font-semibold px-5 py-2.5 text-right">Escalations</th>
                  <th className="font-semibold px-5 py-2.5">Top topic</th>
                </tr>
              </thead>
              <tbody>
                {data.by_community.map((c) => (
                  <tr key={c.tenant_id} data-testid={`intel-row-${c.tenant_id}`}
                    onClick={() => scoped && enterCommunity && enterCommunity(c.tenant_id)}
                    className={`border-b border-sand-50 last:border-0 hover:bg-sand-50/60 ${scoped ? "cursor-pointer" : ""}`}>
                    <td className="px-5 py-3 font-semibold text-ink">{c.name}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{c.conversations}</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <span className={c.escalations > 0 ? "text-amber-700 font-semibold" : "text-ink/40"}>{c.escalations}</span>
                    </td>
                    <td className="px-5 py-3 text-ink/70">{c.top_topic}</td>
                  </tr>
                ))}
                {!data.by_community.length && <tr><td colSpan={4} className="px-5 py-8 text-center text-ink/40">No activity in this window.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Topic drill-down */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent data-testid="topic-drill-dialog" className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><MessagesSquare className="h-5 w-5 text-redline-500" /> {drill?.label} conversations</DialogTitle>
            <DialogDescription>The actual resident conversations tagged “{drill?.label}” in the last {days} days. Tap one to read the full thread.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto -mx-6 px-6">
            {drillLoading ? (
              <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
            ) : (drillData?.conversations || []).length === 0 ? (
              <div className="text-center text-ink/40 py-12 text-sm">No conversations in this window.</div>
            ) : (
              <div className="space-y-2">
                {drillData.conversations.map((c) => (
                  <button key={c.conversation_id} data-testid={`drill-conv-${c.conversation_id}`} onClick={() => setSelConv(c.conversation_id)}
                    className="w-full text-left rounded-xl border border-sand-200 p-3 hover:border-redline-300 hover:bg-sand-50/60 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink truncate">{c.name}</span>
                      <span className="text-[11px] text-ink/40 shrink-0">{fmtDate(c.created_at)}</span>
                    </div>
                    <div className="text-[11px] text-ink/45 mt-0.5">{!scoped ? `${c.tenant_name} · ` : ""}{c.phone}</div>
                    {c.snippet && <div className="text-sm text-ink/70 mt-1 line-clamp-2">“{c.snippet}”</div>}
                    {c.escalation_reason && <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><AlertTriangle className="h-2.5 w-2.5" /> {c.escalation_reason.replace(/_/g, " ")}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConversationView convId={selConv} role={actualRole || "super_admin"} open={!!selConv} onClose={() => setSelConv(null)} onChanged={() => {}} />
    </div>
  );
}
